/**
 * Appearance preference: light, dark, or follow the system. Stored under the
 * same `bridge.theme` key the `?theme=` URL switch writes, and applied to
 * `<html data-theme>` the way main.tsx always has.
 */
export type Appearance = 'light' | 'dark' | 'system'

const KEY = 'bridge.theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'
let watching = false

function isAppearance(value: string | null): value is Appearance {
  return value === 'light' || value === 'dark' || value === 'system'
}

export function readAppearance(): Appearance {
  try {
    const stored = localStorage.getItem(KEY)
    return isAppearance(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function resolveAppearance(preference: Appearance): 'light' | 'dark' {
  if (preference !== 'system') return preference
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

/** Apply a preference to the document; `system` keeps following the OS. */
export function applyAppearance(preference: Appearance = readAppearance()) {
  const resolved = resolveAppearance(preference)
  if (resolved === 'light') document.documentElement.dataset.theme = 'light'
  else delete document.documentElement.dataset.theme
  if (!watching) {
    watching = true
    window.matchMedia(DARK_QUERY).addEventListener('change', () => {
      if (readAppearance() === 'system') applyAppearance('system')
    })
  }
}

/** Apply a valid URL preference and discard obsolete stored values. */
export function initializeAppearance(requested: string | null) {
  try {
    const stored = localStorage.getItem(KEY)
    if (isAppearance(requested)) localStorage.setItem(KEY, requested)
    else if (stored !== null && !isAppearance(stored))
      localStorage.removeItem(KEY)
  } catch {
    // Storage can be unavailable in private browsing; system remains the default.
  }
  applyAppearance()
}

export function saveAppearance(preference: Appearance) {
  try {
    if (preference === 'light') localStorage.setItem(KEY, 'light')
    else localStorage.setItem(KEY, preference)
  } catch {
    // Private mode: the choice lasts for this page only.
  }
  applyAppearance(preference)
}
